import { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/layout/AppShell";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useBusiness } from "@/hooks/use-business";
import { supabase } from "@/integrations/supabase/client";
import { Plus, Loader2, Search, UserPlus, RefreshCw, X, Pencil, Trash2, TrendingUp } from "lucide-react";
import { StatusBadge } from "@/components/ui/status-badge";
import { addDays, format, parseISO, subDays } from "date-fns";
import { useAuth } from "@/contexts/AuthContext";
import { phoneToE164 } from "@/lib/phone-utils";
import { buildPaystackCallbackUrl } from "@/lib/payments";

type MembershipPlan = {
  id: string;
  business_id: string;
  name: string;
  interval: "monthly" | "quarterly" | "yearly" | "custom";
  price_amount: number;
  currency: string;
  custom_days?: number | null;
  active: boolean;
  created_at: string;
  updated_at: string;
};

type MembershipRow = {
  id: string;
  business_id: string;
  customer_id: string;
  plan_id: string;
  membership_code: string;
  status: "active" | "expired" | "suspended" | "cancelled";
  starts_at: string;
  expires_at: string;
  created_at: string;
  updated_at: string;
};

export default function Memberships() {
  const { toast } = useToast();
  const { business, loading: businessLoading } = useBusiness();
  const { user } = useAuth();
  const sb = supabase as any;

  const [plans, setPlans] = useState<MembershipPlan[]>([]);
  const [memberships, setMemberships] = useState<(MembershipRow & { customer?: any; plan?: any })[]>([]);
  const [loading, setLoading] = useState(true);
  const [revenueThisMonth, setRevenueThisMonth] = useState<number>(0);

  const [query, setQuery] = useState("");
  const [isPlanDialogOpen, setIsPlanDialogOpen] = useState(false);
  const [isSavingPlan, setIsSavingPlan] = useState(false);
  const [editingPlanId, setEditingPlanId] = useState<string | null>(null);
  const [planForm, setPlanForm] = useState({
    name: "",
    interval: "monthly" as "monthly" | "quarterly" | "yearly" | "custom",
    price_amount: "",
    currency: "GHS",
    custom_days: "",
  });

  const [isAddMemberOpen, setIsAddMemberOpen] = useState(false);
  const [customerQuery, setCustomerQuery] = useState("");
  const [customerResults, setCustomerResults] = useState<any[]>([]);
  const [customerSearching, setCustomerSearching] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<any | null>(null);
  const [createNewCustomer, setCreateNewCustomer] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ name: "", phone: "", email: "" });
  const [selectedPlanId, setSelectedPlanId] = useState<string>("");
  const [startsAt, setStartsAt] = useState<string>(new Date().toISOString().slice(0, 10));
  const [isCreatingMember, setIsCreatingMember] = useState(false);
  const [renewingMembershipId, setRenewingMembershipId] = useState<string | null>(null);
  const [cancellingMembershipId, setCancellingMembershipId] = useState<string | null>(null);

  const filteredMemberships = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return memberships;
    return memberships.filter((m) => {
      const code = String((m as any).membership_code || "").toLowerCase();
      const name = String((m as any).customer?.name || "").toLowerCase();
      const email = String((m as any).customer?.email || "").toLowerCase();
      const phone = String((m as any).customer?.phone || "").toLowerCase();
      return code.includes(q) || name.includes(q) || email.includes(q) || phone.includes(q);
    });
  }, [memberships, query]);

  const summary = useMemo(() => {
    const today = new Date();
    const todayStr = format(today, "yyyy-MM-dd");
    const todayDate = parseISO(todayStr);
    const expiringUntil = addDays(todayDate, 7);
    const churnFrom = subDays(todayDate, 30);

    const isActive = (m: any) => {
      const exp = m?.expires_at ? parseISO(String(m.expires_at)) : null;
      return m?.status === "active" && exp && exp >= todayDate;
    };

    const isExpiringSoon = (m: any) => {
      if (!m.expires_at) return false;
      if (m.status !== "active") return false;
      const exp = parseISO(String(m.expires_at));
      // Compare dates only (ignore time)
      const expDateOnly = new Date(exp.getFullYear(), exp.getMonth(), exp.getDate());
      const todayDateOnly = new Date(todayDate.getFullYear(), todayDate.getMonth(), todayDate.getDate());
      const expiringUntilDateOnly = new Date(expiringUntil.getFullYear(), expiringUntil.getMonth(), expiringUntil.getDate());
      return expDateOnly >= todayDateOnly && expDateOnly <= expiringUntilDateOnly;
    };

    const isChurned = (m: any) => {
      const exp = m?.expires_at ? parseISO(String(m.expires_at)) : null;
      const expiredByDate = !!exp && exp < todayDate;
      const churnStatus = m?.status === "expired" || m?.status === "cancelled";
      // "Churn" here means memberships that are no longer active.
      return churnStatus || expiredByDate;
    };

    const activeCount = memberships.filter(isActive).length;
    const expiringCount = memberships.filter(isExpiringSoon).length;

    // last 30 days churned (by expiry date if present), otherwise by status
    const churnCount = memberships.filter((m: any) => {
      const exp = m?.expires_at ? parseISO(String(m.expires_at)) : null;
      const inWindow = !!exp && exp >= churnFrom && exp < todayDate;
      const churnStatus = m?.status === "expired" || m?.status === "cancelled";
      return inWindow || churnStatus;
    }).length;

    return { activeCount, expiringCount, churnCount };
  }, [memberships]);

  const fetchAll = async () => {
    if (!business?.id) return;
    try {
      setLoading(true);
      const now = new Date();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
      const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59).toISOString();

      const [
        { data: plansData, error: pErr },
        { data: memData, error: mErr },
        { data: paymentsData, error: payErr },
      ] = await Promise.all([
        sb
          .from("membership_plans")
          .select("*")
          .eq("business_id", business.id)
          .order("created_at", { ascending: false }),
        sb
          .from("memberships")
          .select("*, customer:customers(id,name,email,phone), plan:membership_plans(id,name,interval,price_amount,currency)")
          .eq("business_id", business.id)
          .order("created_at", { ascending: false }),
        sb
          .from("platform_payments")
          .select("gross_amount")
          .eq("business_id", business.id)
          .eq("context", "membership_payment")
          .eq("status", "success")
          .gte("paid_at", monthStart)
          .lte("paid_at", monthEnd),
      ]);
      if (pErr) throw pErr;
      if (mErr) throw mErr;
      if (payErr) throw payErr;
      setPlans((plansData || []) as any);
      setMemberships((memData || []) as any);
      const total = (paymentsData || []).reduce((sum: number, p: any) => sum + Number(p.gross_amount || 0), 0);
      setRevenueThisMonth(total);
    } catch (e: any) {
      toast({ title: "Failed to load memberships", description: e?.message || "Try again.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!business?.id) return;
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business?.id]);

  const openEditPlan = (plan: MembershipPlan) => {
    setEditingPlanId(plan.id);
    setPlanForm({
      name: plan.name,
      interval: plan.interval,
      price_amount: String(plan.price_amount || 0),
      currency: plan.currency || currency,
      custom_days: plan.custom_days ? String(plan.custom_days) : "",
    });
    setIsPlanDialogOpen(true);
  };

  const resetPlanForm = () => {
    setEditingPlanId(null);
    setPlanForm({ name: "", interval: "monthly", price_amount: "", currency: currency, custom_days: "" });
  };

  const savePlan = async () => {
    if (!business?.id) return;
    if (!planForm.name.trim()) return;
    if (planForm.interval === "custom" && (!planForm.custom_days || Number(planForm.custom_days) <= 0)) {
      toast({
        title: "Custom days required",
        description: "Please enter the number of days for the custom interval.",
        variant: "destructive",
      });
      return;
    }
    setIsSavingPlan(true);
    try {
      const price = Number(planForm.price_amount || 0);
      const customDays = planForm.interval === "custom" ? Number(planForm.custom_days) : null;
      const planData: any = {
        name: planForm.name.trim(),
        interval: planForm.interval as any,
        price_amount: price,
        currency: planForm.currency,
      };
      
      // Only include custom_days if it's a custom interval
      // This prevents errors if the column doesn't exist yet
      if (planForm.interval === "custom" && customDays) {
        planData.custom_days = customDays;
      } else if (planForm.interval !== "custom") {
        // Set to null for non-custom intervals to clear any existing value
        planData.custom_days = null;
      }
      
      if (editingPlanId) {
        // Update existing plan
        const { error } = await sb
          .from("membership_plans")
          .update(planData)
          .eq("id", editingPlanId);
        if (error) throw error;
        toast({ title: "Plan updated", description: "Membership plan has been updated." });
      } else {
        // Create new plan
        const { error } = await sb.from("membership_plans").insert({
          ...planData,
          business_id: business.id,
        active: true,
        });
      if (error) throw error;
      toast({ title: "Plan created", description: "Membership plan has been added." });
      }
      setIsPlanDialogOpen(false);
      resetPlanForm();
      await fetchAll();
    } catch (e: any) {
      toast({
        title: editingPlanId ? "Failed to update plan" : "Failed to create plan",
        description: e?.message || "Try again.",
        variant: "destructive",
      });
    } finally {
      setIsSavingPlan(false);
    }
  };

  const handleDeletePlan = async (plan: MembershipPlan) => {
    if (!business?.id) return;
    if (!window.confirm(`Are you sure you want to delete "${plan.name}"? This action cannot be undone.`)) {
      return;
    }
    try {
      const { error } = await sb.from("membership_plans").delete().eq("id", plan.id);
      if (error) throw error;
      toast({ title: "Plan deleted", description: "Membership plan has been deleted." });
      await fetchAll();
    } catch (e: any) {
      toast({
        title: "Failed to delete plan",
        description: e?.message || "Try again.",
        variant: "destructive",
      });
    }
  };

  const currency = business?.currency || "GHS";

  const formatCurrency = (amount: number) => {
    const symbols: Record<string, string> = {
      GHS: "₵", NGN: "₦", USD: "$", EUR: "€", GBP: "£", ZAR: "R", KES: "KSh",
    };
    const symbol = symbols[currency] ?? currency + " ";
    return `${symbol}${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const searchCustomers = async () => {
    if (!business?.id) return;
    const q = customerQuery.trim();
    if (!q) return;
    setCustomerSearching(true);
    try {
      const { data, error } = await supabase
        .from("customers")
        .select("id,name,email,phone")
        .eq("business_id", business.id)
        .or(`name.ilike.%${q}%,email.ilike.%${q}%,phone.ilike.%${q}%`)
        .order("created_at", { ascending: false })
        .limit(20);
      if (error) throw error;
      setCustomerResults(data || []);
    } catch (e: any) {
      toast({ title: "Customer search failed", description: e?.message || "Try again.", variant: "destructive" });
    } finally {
      setCustomerSearching(false);
    }
  };

  const resetAddMember = () => {
    setCustomerQuery("");
    setCustomerResults([]);
    setSelectedCustomer(null);
    setCreateNewCustomer(false);
    setNewCustomer({ name: "", phone: "", email: "" });
    setSelectedPlanId("");
    setStartsAt(new Date().toISOString().slice(0, 10));
  };

  const createMember = async () => {
    if (!business?.id) return;
    if (!selectedPlanId) {
      toast({ title: "Select a plan", description: "Choose a membership plan first.", variant: "destructive" });
      return;
    }

    setIsCreatingMember(true);
    try {
      let customerId = selectedCustomer?.id as string | undefined;

      if (createNewCustomer) {
        if (!newCustomer.name.trim()) {
          toast({ title: "Name required", description: "Enter the member name.", variant: "destructive" });
          return;
        }
        const normalizedPhone = newCustomer.phone.trim()
          ? (phoneToE164(newCustomer.phone.trim(), "233") || newCustomer.phone.trim())
          : null;
        const { data: cust, error: cErr } = await supabase
          .from("customers")
          .insert({
            business_id: business.id,
            name: newCustomer.name.trim(),
            phone: normalizedPhone,
            email: newCustomer.email.trim() || null,
            notes: "Member",
          } as any)
          .select()
          .single();
        if (cErr) throw cErr;
        customerId = cust.id;
      }

      if (!customerId) {
        toast({ title: "Select a customer", description: "Pick an existing customer or create a new one.", variant: "destructive" });
        return;
      }

      const { error: mErr } = await sb.from("memberships").insert({
        business_id: business.id,
        customer_id: customerId,
        plan_id: selectedPlanId,
        starts_at: startsAt,
        expires_at: null, // computed by trigger
      } as any);
      if (mErr) throw mErr;

      toast({ title: "Member registered", description: "Membership created successfully." });
      setIsAddMemberOpen(false);
      resetAddMember();
      await fetchAll();
    } catch (e: any) {
      toast({ title: "Failed to register member", description: e?.message || "Try again.", variant: "destructive" });
    } finally {
      setIsCreatingMember(false);
    }
  };

  const handleRenewMembership = async (membership: any) => {
    if (!business?.id) return;
    if (!membership?.customer?.email && !membership?.customer?.phone) {
      toast({
        title: "Missing contact info",
        description: "Member must have an email or phone number for renewal.",
        variant: "destructive",
      });
      return;
    }

    const payerEmail = (membership.customer?.email || user?.email || "").trim();
    if (!payerEmail) {
      toast({
        title: "Email required",
        description: "Please provide an email address for payment processing.",
        variant: "destructive",
      });
      return;
    }

    setRenewingMembershipId(membership.id);
    try {
      // Get current session to ensure we have a valid token
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !sessionData?.session) {
        toast({
          title: "Authentication error",
          description: "Please log out and log back in to continue.",
          variant: "destructive",
        });
        return;
      }

      const callbackUrl = buildPaystackCallbackUrl("/app/memberships");
      
      const { data, error } = await supabase.functions.invoke("paystack-init", {
        body: {
          context: { type: "membership_payment", membershipId: membership.id },
          payerEmail,
          payerName: membership.customer?.name || null,
          callbackUrl,
        },
      });
      
      if (error) throw error;
      const url = String((data as any)?.authorizationUrl || "");
      if (!url) throw new Error("Missing Paystack authorizationUrl");
      
      // Safari mobile fix: Use assign() with a small delay to prevent blocking
      setTimeout(() => {
        window.location.assign(url);
      }, 50);
    } catch (e: any) {
      toast({
        title: "Failed to start renewal payment",
        description: e?.message || "Try again.",
        variant: "destructive",
      });
    } finally {
      setRenewingMembershipId(null);
    }
  };

  const isExpiringOrExpired = (m: any) => {
    if (!m.expires_at) return false;
    const expDate = parseISO(m.expires_at);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return expDate <= addDays(today, 7) || m.status === "expired";
  };

  const handleCancelMembership = async (membership: any) => {
    if (!business?.id) return;
    if (!window.confirm(`Are you sure you want to cancel ${membership.customer?.name || "this member"}'s membership? This action cannot be undone.`)) {
      return;
    }

    setCancellingMembershipId(membership.id);
    try {
      const { error } = await sb
        .from("memberships")
        .update({
          status: "cancelled",
          updated_at: new Date().toISOString(),
        } as any)
        .eq("id", membership.id);
      
      if (error) throw error;
      toast({ title: "Membership cancelled", description: "The membership has been cancelled successfully." });
      await fetchAll();
    } catch (e: any) {
      toast({
        title: "Failed to cancel membership",
        description: e?.message || "Try again.",
        variant: "destructive",
      });
    } finally {
      setCancellingMembershipId(null);
    }
  };

  const getStatusBadgeStatus = (status: string) => {
    if (status === "active") return "active";
    if (status === "cancelled") return "cancelled";
    if (status === "expired") return "inactive";
    if (status === "suspended") return "inactive";
    return "inactive";
  };

  return (
    <AppShell businessName={business?.name || "My Business"}>
      <PageHeader
        title="Memberships"
        description="Manage membership plans and members"
        action={
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <Button
              size="pill"
              variant="outline"
              className="w-full sm:w-auto"
              onClick={() => {
                if (plans.length === 0) {
                  toast({ title: "Create a plan first", description: "Add at least one membership plan to register members.", variant: "destructive" });
                  return;
                }
                setIsAddMemberOpen(true);
              }}
              disabled={businessLoading}
            >
              <UserPlus className="mr-2 h-4 w-4" />
              Add Member
            </Button>
            <Button
              size="pill"
              className="w-full sm:w-auto"
              onClick={() => {
                resetPlanForm();
                setIsPlanDialogOpen(true);
              }}
              disabled={businessLoading}
            >
              <Plus className="mr-2 h-4 w-4" />
              New Plan
            </Button>
          </div>
        }
      />

      <div className="mb-6 grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
        <div className="space-y-2">
          <Label>Search members</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by membership ID, name, phone…"
              className="pl-9"
            />
          </div>
        </div>
        <div className="text-sm text-muted-foreground sm:text-right">
          Prefix: <span className="font-medium text-foreground">{(business as any)?.membership_prefix || "MEM"}</span>
        </div>
      </div>

      <div className="mb-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="rounded-xl border border-border bg-card p-3 sm:p-4">
          <p className="text-xs text-muted-foreground">Active</p>
          <p className="mt-2 text-2xl font-semibold text-foreground">{loading ? "…" : summary.activeCount}</p>
          <p className="mt-1 text-xs text-muted-foreground">Members with valid memberships</p>
        </div>
        <div className="rounded-xl border border-border bg-card p-3 sm:p-4">
          <p className="text-xs text-muted-foreground">Expiring soon</p>
          <p className="mt-2 text-2xl font-semibold text-foreground">{loading ? "…" : summary.expiringCount}</p>
          <p className="mt-1 text-xs text-muted-foreground">Next 7 days</p>
        </div>
        <div className="rounded-xl border border-border bg-card p-3 sm:p-4">
          <p className="text-xs text-muted-foreground">Churn</p>
          <p className="mt-2 text-2xl font-semibold text-foreground">{loading ? "…" : summary.churnCount}</p>
          <p className="mt-1 text-xs text-muted-foreground">Expired/cancelled (last 30 days)</p>
        </div>
        <div className="rounded-xl border border-border bg-card p-3 sm:p-4">
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">Revenue this month</p>
            <TrendingUp className="h-3.5 w-3.5 text-muted-foreground" />
          </div>
          <p className="mt-2 text-2xl font-semibold text-foreground">
            {loading ? "…" : formatCurrency(revenueThisMonth)}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">From renewal payments</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="grid gap-4">
          <div className="rounded-xl border border-border bg-card p-3 sm:p-4">
            <div className="flex items-center justify-between mb-3 gap-2">
              <p className="font-semibold text-foreground">Plans</p>
              <p className="text-xs text-muted-foreground">{plans.length} total</p>
            </div>
            {plans.length === 0 ? (
              <p className="text-sm text-muted-foreground">No plans yet. Create one to start enrolling members.</p>
            ) : (
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {plans.map((p) => (
                  <div key={p.id} className="rounded-lg border border-border bg-background p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                    <p className="font-medium text-foreground">{p.name}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {p.interval === "custom" && p.custom_days
                        ? `${p.custom_days} days`
                        : String(p.interval)} • {p.currency || currency} {Number(p.price_amount || 0).toFixed(2)}
                    </p>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openEditPlan(p)}
                          className="h-7 w-7 p-0"
                        >
                          <Pencil className="h-3 w-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeletePlan(p)}
                          className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-xl border border-border bg-card p-3 sm:p-4">
            <div className="flex items-center justify-between mb-3 gap-2">
              <p className="font-semibold text-foreground">Members</p>
              <p className="text-xs text-muted-foreground">{filteredMemberships.length} shown</p>
            </div>
            {filteredMemberships.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No members found. (Next: we’ll add “Add member” + renewals + check-ins.)
              </p>
            ) : (
              <div className="grid gap-2">
                {filteredMemberships.map((m: any) => (
                  <div key={m.id} className="rounded-lg border border-border bg-background p-3">
                    {/* Mobile: Name and Status at top */}
                    <div className="flex items-start justify-between gap-2 mb-2 sm:hidden">
                      <p className="font-medium text-foreground break-words flex-1 min-w-0">
                        {m.customer?.name || "Member"}
                      </p>
                      <StatusBadge status={getStatusBadgeStatus(m.status)} />
                    </div>
                    
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-3">
                      <div className="min-w-0 flex-1">
                        {/* Desktop: Name with status inline */}
                        <p className="font-medium text-foreground break-words hidden sm:block">
                          <span className="inline truncate">{m.customer?.name || "Member"}</span>
                          <span className="inline text-muted-foreground font-mono ml-2">
                            {m.membership_code}
                          </span>
                        </p>
                        {/* Mobile: Membership code (name already shown above) */}
                        <p className="font-medium text-foreground break-words sm:hidden">
                          <span className="text-muted-foreground font-mono">
                            {m.membership_code}
                          </span>
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Plan: {m.plan?.name || "—"} • Expires: {m.expires_at}
                        </p>
                        {m.plan?.price_amount && (
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {currency} {Number(m.plan.price_amount).toFixed(2)} / {m.plan.interval}
                          </p>
                        )}
                      </div>
                      {/* Desktop: Status badge */}
                      <div className="hidden sm:flex items-center justify-center gap-2">
                        <StatusBadge status={getStatusBadgeStatus(m.status)} />
                      </div>
                      <div className="flex items-center gap-2 sm:justify-end">
                        {m.status !== "cancelled" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleRenewMembership(m)}
                            disabled={renewingMembershipId === m.id || !m.plan?.price_amount}
                            className="text-xs"
                          >
                            {renewingMembershipId === m.id ? (
                              <>
                                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                Processing...
                              </>
                            ) : (
                              <>
                                <RefreshCw className="mr-1 h-3 w-3" />
                                Renew
                              </>
                            )}
                          </Button>
                        )}
                        {m.status !== "cancelled" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleCancelMembership(m)}
                            disabled={cancellingMembershipId === m.id}
                            className="text-xs text-destructive hover:text-destructive"
                          >
                            {cancellingMembershipId === m.id ? (
                              <>
                                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                Cancelling...
                              </>
                            ) : (
                              <>
                                <X className="mr-1 h-3 w-3" />
                                Cancel
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <Dialog
        open={isPlanDialogOpen}
        onOpenChange={(open) => {
          setIsPlanDialogOpen(open);
          if (!open) resetPlanForm();
        }}
      >
        <DialogContent className="sm:max-w-md w-[calc(100%-2rem)] max-h-[90vh] p-0 gap-0 overflow-hidden rounded-2xl flex flex-col">
          <DialogHeader className="px-6 pt-6 pb-4 flex-shrink-0">
            <DialogTitle>{editingPlanId ? "Edit membership plan" : "Create membership plan"}</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto px-6 pb-4 flex-1 min-h-0">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input value={planForm.name} onChange={(e) => setPlanForm((p) => ({ ...p, name: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Currency</Label>
                <Select value={planForm.currency} onValueChange={(v) => setPlanForm((p) => ({ ...p, currency: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="GHS">Ghanaian Cedi (GHS) - ₵</SelectItem>
                    <SelectItem value="NGN">Nigerian Naira (NGN) - ₦</SelectItem>
                    <SelectItem value="USD">US Dollar (USD) - $</SelectItem>
                    <SelectItem value="EUR">Euro (EUR) - €</SelectItem>
                    <SelectItem value="GBP">British Pound (GBP) - £</SelectItem>
                    <SelectItem value="ZAR">South African Rand (ZAR) - R</SelectItem>
                    <SelectItem value="KES">Kenyan Shilling (KES) - KSh</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Interval</Label>
                  <Select value={planForm.interval} onValueChange={(v) => setPlanForm((p) => ({ ...p, interval: v as any }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="yearly">Yearly</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Price ({planForm.currency})</Label>
                  <Input
                    type="number"
                    value={planForm.price_amount}
                    onChange={(e) => setPlanForm((p) => ({ ...p, price_amount: e.target.value }))}
                  />
                </div>
              </div>
              {planForm.interval === "custom" && (
                <div className="space-y-2">
                  <Label>Number of Days</Label>
                  <Input
                    type="number"
                    value={planForm.custom_days}
                    onChange={(e) => setPlanForm((p) => ({ ...p, custom_days: e.target.value }))}
                    placeholder="e.g., 45, 90, 180"
                    min="1"
                  />
                  <p className="text-xs text-muted-foreground">Enter the number of days for this custom membership period.</p>
                </div>
              )}
              <Button className="w-full rounded-lg" onClick={savePlan} disabled={isSavingPlan}>
                {isSavingPlan ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : editingPlanId ? (
                  "Update plan"
                ) : (
                  "Create plan"
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isAddMemberOpen}
        onOpenChange={(open) => {
          setIsAddMemberOpen(open);
          if (!open) resetAddMember();
        }}
      >
        <DialogContent className="sm:max-w-lg w-[calc(100%-2rem)] max-h-[90vh] p-0 gap-0 overflow-hidden rounded-2xl flex flex-col">
          <DialogHeader className="px-6 pt-6 pb-4 flex-shrink-0">
            <DialogTitle>Register a member</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto px-6 pb-4 flex-1 min-h-0">
            <div className="space-y-4">
            <div className="space-y-2">
              <Label>Plan</Label>
              <Select value={selectedPlanId} onValueChange={setSelectedPlanId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a plan" />
                </SelectTrigger>
                <SelectContent>
                  {plans.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} • {currency} {Number(p.price_amount || 0).toFixed(2)} / {p.interval}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Start date</Label>
              <Input type="date" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
              <p className="text-xs text-muted-foreground">Expiry is calculated automatically from the plan interval.</p>
            </div>

            <div className="rounded-lg border border-border bg-accent/30 p-3">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium text-foreground">Customer</p>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setCreateNewCustomer((v) => !v);
                    setSelectedCustomer(null);
                  }}
                >
                  {createNewCustomer ? "Search existing" : "Create new"}
                </Button>
              </div>

              {createNewCustomer ? (
                <div className="mt-3 grid gap-3">
                  <div className="space-y-1">
                    <Label>Name</Label>
                    <Input value={newCustomer.name} onChange={(e) => setNewCustomer((c) => ({ ...c, name: e.target.value }))} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label>Phone</Label>
                      <Input value={newCustomer.phone} onChange={(e) => setNewCustomer((c) => ({ ...c, phone: e.target.value }))} />
                    </div>
                    <div className="space-y-1">
                      <Label>Email</Label>
                      <Input value={newCustomer.email} onChange={(e) => setNewCustomer((c) => ({ ...c, email: e.target.value }))} />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mt-3 space-y-2">
                  <div className="flex gap-2">
                    <Input
                      value={customerQuery}
                      onChange={(e) => setCustomerQuery(e.target.value)}
                      placeholder="Search by name, phone, email…"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") searchCustomers();
                      }}
                    />
                    <Button onClick={searchCustomers} disabled={customerSearching || !customerQuery.trim()} variant="outline">
                      {customerSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : "Search"}
                    </Button>
                  </div>

                  {selectedCustomer ? (
                    <div className="rounded-md border border-border bg-background p-3">
                      <p className="text-sm font-medium text-foreground">{selectedCustomer.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {selectedCustomer.email || "—"} • {selectedCustomer.phone || "—"}
                      </p>
                      <Button size="sm" variant="ghost" className="mt-2" onClick={() => setSelectedCustomer(null)}>
                        Change
                      </Button>
                    </div>
                  ) : customerResults.length > 0 ? (
                    <div className="max-h-48 overflow-auto rounded-md border border-border bg-background">
                      {customerResults.map((c) => (
                        <button
                          type="button"
                          key={c.id}
                          className="w-full text-left px-3 py-2 hover:bg-accent/50 border-b border-border last:border-b-0"
                          onClick={() => setSelectedCustomer(c)}
                        >
                          <p className="text-sm font-medium text-foreground">{c.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {c.email || "—"} • {c.phone || "—"}
                          </p>
                        </button>
                      ))}
                    </div>
                  ) : customerQuery.trim() ? (
                    <p className="text-xs text-muted-foreground">No customers found. You can “Create new”.</p>
                  ) : null}
                </div>
              )}
            </div>

            <Button className="w-full" onClick={createMember} disabled={isCreatingMember}>
              {isCreatingMember ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                "Register member"
              )}
            </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}


